<template>
  <InputGroup class="w-100">
    <InputGroupAddon v-if="$slots['prepend'] != null" class="p-0">
      <slot name="prepend" />
    </InputGroupAddon>
    <Select
      v-model="modelValue"
      :options="options"
      option-value="id"
      option-label="label"
      :option-group-label="optionGroupLabel"
      :option-group-children="optionGroupChildren ?? 'group'"
      :placeholder="placeholder"
      :show-clear="showClear"
      :disabled="disabled"
      fluid
      filter
      filter-icon="fas fa-search"
      clear-icon="fas fa-times"
      :filter-locale="locale"
      :reset-filter-on-hide="true"
      :scroll-height="scrollHeight"
      :virtual-scroller-options="{
        itemSize: getRemSize() * 2.155,
      }"
      :class="state == null ? undefined : state ? 'p-valid' : 'p-invalid'"
      :auto-filter-focus="true"
      :auto-option-focus="false"
      :data-testid="dataTestid"
      :pt="{
        pcFilter: {
          root: {
            'data-testid': `${dataTestid}|Filter`,
          },
        },
        overlay: {
          'data-testid': `${dataTestid}|Overlay`,
        },
        listContainer: {
          'data-testid': `${dataTestid}|Overlay`,
        },
      }"
      :empty-message="t('components.shared.searchSelect.BaseSelect.noResultPossibleString')"
      @filter="searchString = $event.value"
      @show="$emit('open')"
      @hide="($emit('close'), (searchString = ''))"
    >
      <template #value="slotProps">
        <span v-if="valueOverlayLabel != null" class="value-overlay-label">
          {{ valueOverlayLabel }}
        </span>
        <div v-else-if="slotProps.value != null && modelValue != null" class="select-option">
          <i v-if="selectedOption?.icon" :class="selectedOption.icon" aria-hidden="true" />
          <span>{{ selectedOption?.label }}</span>
        </div>
        <span v-else> {{ slotProps.placeholder ?? "&nbsp;" }} </span>
      </template>
      <template #option="{ option }">
        <div class="select-option">
          <i v-if="option.icon" :class="option.icon" aria-hidden="true" />
          <span>{{ option.label }}</span>
        </div>
      </template>
      <template #footer>
        <slot name="footer" :search-string="searchString">
          <div v-if="allowCreate" class="px-3 pt-1 pb-2 text-xs">
            <T
              v-if="searchString.length > 0"
              :key="searchString"
              key-name="components.lSelect.wantToCreate"
              ns="app"
            >
              <template #searchString>
                <a
                  href="#"
                  :data-testid="`${dataTestid}|CreateNew`"
                  @click.prevent="$emit('create', searchString)"
                  >{{ searchString || "" }}</a
                >
              </template>
            </T>
            <a v-else> &nbsp; </a>
          </div>
        </slot>
      </template>
    </Select>
    <InputGroupAddon
      v-if="linkToOrigin != null && !hideJumpToOrigin && !isOptionDisabled"
      class="p-0"
      :class="state == null ? undefined : state ? 'p-valid' : 'p-invalid'"
    >
      <LISAButton
        text
        class="border-radius-right"
        size="small"
        severity="secondary"
        icon="fas fa-arrow-right"
        :disabled="loading || modelValue == null || disabled"
        @click="
          $router.push({
            ...linkToOrigin,
            params: { ...linkToOrigin.params, id: modelValue },
          })
        "
      />
    </InputGroupAddon>
  </InputGroup>
</template>

<script setup lang="ts" generic="T extends number | string = number">
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import Select from "primevue/select";
import { useTranslation } from "@/language/translation";
import T from "@/language/StaticTranslation";
import LISAButton from "../../primevue/LISAButton.vue";
import type { RouteLocationNamedRaw } from "vue-router";
import { computed, ref } from "vue";
type SingleSelectOption = {
  id?: T;
  label: string;
  icon?: string;
  disabled?: boolean;
  group?: string | SingleSelectOption[];
  items?: SingleSelectOption[];
};

const modelValue = defineModel<T | null>({ default: null });

const props = defineProps<{
  options: SingleSelectOption[];
  showClear?: boolean;
  loading: boolean;
  disabled?: boolean;
  dataTestid?: string;
  linkToOrigin?: RouteLocationNamedRaw;
  allowCreate?: boolean;
  placeholder?: string;
  state?: boolean | null;
  valueOverlayLabel?: string;
  hideJumpToOrigin?: boolean;
  optionGroupLabel?: string;
  optionGroupChildren?: "group" | "items";
}>();

defineEmits<{
  create: [searchString: string];
  open: [];
  close: [];
}>();

const flatOptions = computed(() =>
  props.options.flatMap((option) => {
    const children = option[props.optionGroupChildren ?? "group"];
    return Array.isArray(children) ? children : [option];
  }),
);
const selectedOption = computed(() =>
  flatOptions.value.find((option) => option.id === modelValue.value),
);

const isOptionDisabled = computed(() => {
  const option = flatOptions.value.find((opt) => opt.id === modelValue.value);
  return option?.disabled ?? false;
});

const searchString = ref<string>("");

function getRemSize(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize);
}
const scrollHeight = computed(() => {
  const groupLabelCount = props.optionGroupLabel == null ? 0 : props.options.length;
  const count = Math.min(13, flatOptions.value.length + groupLabelCount);
  return `min(calc(${count * (getRemSize() * 2.155)}px + var(--p-list-gap) * ${count - 1} + 8px), 50vh)`;
});

const { locale } = useTranslation();

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.border-radius-right {
  border-top-right-radius: var(--p-border-radius-md);
  border-bottom-right-radius: var(--p-border-radius-md);
}

.value-overlay-label {
  color: var(--p-red-500);
}

.select-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
